import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useFileScan } from 'hooks/useFileScan';
import { useGTM } from 'hooks/useGTM';

export type ScanOptions = {
    multiscanning: boolean;
    deepCdr: boolean;
    adaptiveSandbox: boolean;
    proactiveDlp: boolean;
    unarchiving: boolean;
    privateScan: boolean;
};

export type ScanOptionKey = keyof ScanOptions;

export const defaultScanOptions: ScanOptions = {
    multiscanning: true,
    deepCdr: false,
    adaptiveSandbox: false,
    proactiveDlp: false,
    unarchiving: false,
    privateScan: false,
};

/**
 * Defaults for guests / non-paid users: every engine enabled to showcase the full
 * pipeline. Private Scan stays off since it remains a paid-only capability.
 */
export const freeScanOptions: ScanOptions = {
    multiscanning: true,
    deepCdr: true,
    adaptiveSandbox: true,
    proactiveDlp: true,
    unarchiving: true,
    privateScan: false,
};

/**
 * Build the comma-separated rules string consumed by the file scan API.
 * Exported because some callers need rules ahead of scan invocation.
 */
export const buildScanRules = (scanOptions: ScanOptions): string => {
    const isCdrOnly =
        scanOptions.deepCdr && !scanOptions.multiscanning && !scanOptions.proactiveDlp && !scanOptions.adaptiveSandbox;
    const shouldSanitizeForCdr =
        scanOptions.deepCdr &&
        (scanOptions.multiscanning ||
            scanOptions.proactiveDlp ||
            scanOptions.unarchiving ||
            scanOptions.adaptiveSandbox);

    if (isCdrOnly) return 'cdr';
    const rules: string[] = [];
    if (shouldSanitizeForCdr) {
        rules.push('sanitize');
        if (scanOptions.proactiveDlp) rules.push('dlp');
    } else {
        if (scanOptions.multiscanning) rules.push('multiscan');
        if (scanOptions.proactiveDlp) rules.push('dlp');
    }
    if (scanOptions.unarchiving) rules.push('unarchive');
    return rules.join(', ');
};

type UseFileScanFlowOptions = {
    initialOptions?: ScanOptions;
    /** localStorage key for persisting scanOptions. If omitted, state is in-memory only. */
    persistKey?: string;
};

const readPersistedOptions = (key: string | undefined, fallback: ScanOptions): ScanOptions => {
    if (!key || typeof window === 'undefined') return fallback;
    try {
        const saved = localStorage.getItem(key);
        if (!saved) return fallback;
        return { ...fallback, ...JSON.parse(saved) };
    } catch {
        return fallback;
    }
};

/**
 * Shared file-scan flow used by AnalysisFileCard (authenticated) and PublicScanCard (public landing).
 *
 * Covers:
 *  - scan options state + persistence
 *  - the multiscanning <-> deepCdr <-> unarchiving interlock
 *  - the buildRules string
 *  - file input ref / click-trigger / change handler
 *  - submitting the file via useFileScan + tracking progress
 */
export const useFileScanFlow = (options: UseFileScanFlowOptions = {}) => {
    const { initialOptions = defaultScanOptions, persistKey } = options;

    const { scan, isLoading } = useFileScan();
    const { trackCustomEvent } = useGTM();
    const fileInputRef = useRef<HTMLInputElement>(null);
    const abortControllerRef = useRef<AbortController | null>(null);
    const isMountedRef = useRef(true);

    const [scanOptions, setScanOptions] = useState<ScanOptions>(() => readPersistedOptions(persistKey, initialOptions));
    const [isUploaded, setIsUploaded] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [fileName, setFileName] = useState('');
    const [fileSize, setFileSize] = useState(0);

    useEffect(() => {
        if (!persistKey || typeof window === 'undefined') return;
        localStorage.setItem(persistKey, JSON.stringify(scanOptions));
    }, [persistKey, scanOptions]);

    useEffect(() => {
        return () => {
            isMountedRef.current = false;
            abortControllerRef.current?.abort();
        };
    }, []);

    const isUnarchivingDisabled = scanOptions.deepCdr && !scanOptions.multiscanning;

    const handleCheckboxChange = useCallback((option: ScanOptionKey) => {
        setScanOptions((prev) => {
            const next = { ...prev, [option]: !prev[option] };
            if (option === 'deepCdr' && next.deepCdr && !next.multiscanning) next.unarchiving = true;
            if (option === 'multiscanning' && !next.multiscanning && next.deepCdr) next.unarchiving = true;
            return next;
        });
    }, []);

    type SubmitFileExtras = {
        rulesOverride?: string;
        archivePasswords?: string[];
        sendArchivePasswords?: boolean;
    };

    const submitFile = useCallback(
        (file: File, extras: SubmitFileExtras = {}) => {
            const { rulesOverride, archivePasswords = [], sendArchivePasswords = false } = extras;
            const rules = rulesOverride ?? buildScanRules(scanOptions);

            // Switching tabs unmounts the card; abort any in-flight upload to avoid
            // state updates and analytics firing after unmount.
            abortControllerRef.current?.abort();
            const controller = new AbortController();
            abortControllerRef.current = controller;

            setIsUploaded(true);
            setUploadProgress(0);
            setFileName(file.name);
            setFileSize(file.size);

            // Track submission once; progress can fire many times.
            trackCustomEvent('file_upload', { file_type: file.type, file_size: file.size, success: true });

            return scan(
                file,
                rules,
                scanOptions.adaptiveSandbox,
                scanOptions.privateScan,
                archivePasswords,
                (progress: number) => {
                    if (!isMountedRef.current || controller.signal.aborted) return;
                    setUploadProgress(progress);
                },
                undefined,
                sendArchivePasswords,
                controller.signal,
            );
        },
        [scan, scanOptions, trackCustomEvent],
    );

    const handleFileSelect = useCallback(
        (files: FileList | null, extras?: SubmitFileExtras) => {
            if (files && files.length > 0) {
                void submitFile(files[0], extras);
            }
        },
        [submitFile],
    );

    const triggerFileInput = useCallback(() => {
        setIsUploaded(true);
        fileInputRef.current?.click();
    }, []);

    const handleFileInputChange = useCallback(
        (e: React.ChangeEvent<HTMLInputElement>, extras?: SubmitFileExtras) => {
            const files = e.target.files;
            if (files && files.length > 0) handleFileSelect(files, extras);
            else setIsUploaded(false);
            e.target.value = '';
        },
        [handleFileSelect],
    );

    return {
        // option state
        scanOptions,
        setScanOptions,
        handleCheckboxChange,
        isUnarchivingDisabled,
        // upload progress state
        isLoading,
        isUploaded,
        setIsUploaded,
        uploadProgress,
        fileName,
        fileSize,
        // file input / submit
        fileInputRef,
        triggerFileInput,
        handleFileInputChange,
        handleFileSelect,
        submitFile,
    };
};
